import React, { useMemo, useState } from 'react';
import { useListCleaningCompletionsQuery } from '../../store/api/localApi';
import { useLocationContext } from '../../hooks/useLocationContext';
import { CleaningAreaWithStatus, CleaningCompletion, CleaningFrequency } from '../../types';
import { dublinDateString, formatDublinDateTime, formatDublinTime } from '../../utils/date';
import { frequencyLabel, groupByZone } from './CleaningSheet';
import './cleaning-print.css';

// ─── pure helpers (tested in CleaningRecords.helpers.vitest.test.ts) ──────────

/** "Aisling Gallagher" → "AG". First letter of the first two words. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/** `2026-09` → first/last day as date-only strings + the number of days. */
export function monthBounds(month: string): { from: string; to: string; days: number } {
  const [y, m] = month.split('-').map(Number);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate(); // day 0 of next month
  return { from: `${month}-01`, to: `${month}-${String(days).padStart(2, '0')}`, days };
}

const pad2 = (n: number) => String(n).padStart(2, '0');

// ─── chart rows ───────────────────────────────────────────────────────────────

interface ChartRow {
  areaId: string;
  name: string;
  zone?: string;
  /** Undefined for a line removed from the sheet that still has ticks this month. */
  frequency?: CleaningFrequency;
}

/**
 * Rows = every active line ∪ every line that has a completion this month.
 * A line removed mid-month must still print — the record is what was cleaned,
 * not what is currently on the sheet. Removed lines use the snapshot
 * name/room stored on the completion.
 */
function chartRows(areas: CleaningAreaWithStatus[], completions: CleaningCompletion[]): ChartRow[] {
  const rows = new Map<string, ChartRow>();
  for (const a of areas) {
    rows.set(a.id, { areaId: a.id, name: a.name, zone: a.zone, frequency: a.frequency });
  }
  for (const c of completions) {
    if (!rows.has(c.areaId)) {
      rows.set(c.areaId, { areaId: c.areaId, name: c.areaName, zone: c.areaZone });
    }
  }
  return Array.from(rows.values());
}

// ─── CleaningRecords ──────────────────────────────────────────────────────────

interface CleaningRecordsProps {
  locationId: string;
  areas: CleaningAreaWithStatus[];
}

const CleaningRecords: React.FC<CleaningRecordsProps> = ({ locationId, areas }) => {
  // The printed chart is an inspector's copy: it must say which setting it is for.
  const { activeLocationName } = useLocationContext();

  // ── Month chart ──
  const [month, setMonth] = useState(() => dublinDateString().slice(0, 7));
  const { from: chartFrom, to: chartTo, days } = monthBounds(month);
  const { data: chartData, isLoading: chartLoading } = useListCleaningCompletionsQuery({
    locationId,
    from: chartFrom,
    to: chartTo,
  });
  const chartCompletions = chartData?.completions ?? [];

  // areaId → DD → completions that day (Dublin calendar day).
  const cells = useMemo(() => {
    const map = new Map<string, CleaningCompletion[]>();
    for (const c of chartCompletions) {
      const key = `${c.areaId}#${dublinDateString(new Date(c.cleanedAt)).slice(8, 10)}`;
      const bucket = map.get(key);
      if (bucket) bucket.push(c);
      else map.set(key, [c]);
    }
    return map;
  }, [chartCompletions]);

  const rows = chartRows(areas, chartCompletions);
  const grouped = groupByZone(rows);
  const dayNumbers = Array.from({ length: days }, (_, i) => i + 1);
  const monthTitle = new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-IE', {
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Dublin',
  });

  // ── Export table ──
  const today = dublinDateString();
  const thirtyDaysAgo = dublinDateString(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [exportFrom, setExportFrom] = useState(thirtyDaysAgo);
  const [exportTo, setExportTo] = useState(today);
  // Date-only ISO strings compare correctly as strings. Refuse the request
  // client-side; the handler refuses it too (400) for anyone bypassing the UI.
  const rangeInvalid = exportFrom > exportTo;
  const {
    data: exportData,
    isError: exportIsError,
    error: exportError,
  } = useListCleaningCompletionsQuery(
    { locationId, from: exportFrom, to: exportTo },
    { skip: rangeInvalid }
  );
  const exportRejected = exportIsError && (exportError as { status?: number })?.status === 400;
  const completions = exportData?.completions ?? [];

  return (
    <div data-testid="cleaning-records" className="space-y-6">
      {/* ── Month chart ── */}
      <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3 cleaning-print-hide">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="cleaning-chart-month-input"
              className="text-xs text-gray-500 font-medium"
            >
              Month
            </label>
            <input
              id="cleaning-chart-month-input"
              type="month"
              data-testid="cleaning-chart-month"
              value={month}
              onChange={(e) => e.target.value && setMonth(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            type="button"
            data-testid="cleaning-chart-print"
            onClick={() => window.print()}
            className="ml-auto inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            Print
          </button>
        </div>

        {/* Print-only header: on screen the picker says which month. */}
        <div className="cleaning-print-only hidden" data-testid="cleaning-print-header">
          <h2 className="text-base font-semibold">
            Cleaning record — {activeLocationName ? `${activeLocationName} — ` : ''}
            {monthTitle}
          </h2>
        </div>

        {chartLoading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p data-testid="cleaning-chart-empty" className="text-sm text-gray-400">
            Nothing on the sheet and nothing recorded in {monthTitle}.
          </p>
        ) : (
          <div className="overflow-x-auto cleaning-chart-scroll">
            <table data-testid="cleaning-chart" className="text-xs border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white text-left py-1 pr-3 font-semibold text-gray-600 min-w-[180px]">
                    {monthTitle}
                  </th>
                  {dayNumbers.map((d) => (
                    <th key={d} className="w-7 py-1 text-center font-medium text-gray-500">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              {grouped.map(([zone, zoneRows]) => (
                <tbody key={zone || '__ungrouped'}>
                  {zone && (
                    <tr>
                      <th
                        colSpan={days + 1}
                        className="sticky left-0 bg-gray-50 text-left py-1 pr-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                      >
                        {zone}
                      </th>
                    </tr>
                  )}
                  {zoneRows.map((row) => (
                    <tr
                      key={row.areaId}
                      data-testid={`cleaning-chart-row-${row.areaId}`}
                      className="border-t border-gray-100"
                    >
                      <td className="sticky left-0 bg-white py-1 pr-3 whitespace-nowrap">
                        <span className="text-gray-900">{row.name}</span>{' '}
                        <span className="text-gray-400">
                          {row.frequency ? frequencyLabel(row.frequency) : 'Removed'}
                        </span>
                      </td>
                      {dayNumbers.map((d) => {
                        const dd = pad2(d);
                        const ticks = cells.get(`${row.areaId}#${dd}`) ?? [];
                        const title = ticks
                          .map((c) => `${c.cleanedByName} · ${formatDublinTime(c.cleanedAt)}`)
                          .join('\n');
                        return (
                          <td
                            key={d}
                            data-testid={`cleaning-chart-cell-${row.areaId}-${dd}`}
                            title={title || undefined}
                            className={`w-7 h-7 text-center border border-gray-100 ${
                              ticks.length ? 'bg-emerald-50 text-emerald-800 font-medium' : ''
                            }`}
                          >
                            {ticks.length ? initialsOf(ticks[0].cleanedByName) : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </section>

      {/* ── Inspection export ── */}
      <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4 cleaning-print-hide">
        <h2 className="text-sm font-semibold text-gray-800">Inspection export</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="cleaning-export-from-input"
              className="text-xs text-gray-500 font-medium"
            >
              From
            </label>
            <input
              id="cleaning-export-from-input"
              type="date"
              data-testid="cleaning-export-from"
              value={exportFrom}
              onChange={(e) => setExportFrom(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="cleaning-export-to-input" className="text-xs text-gray-500 font-medium">
              To
            </label>
            <input
              id="cleaning-export-to-input"
              type="date"
              data-testid="cleaning-export-to"
              value={exportTo}
              onChange={(e) => setExportTo(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {(rangeInvalid || exportRejected) && (
            <p
              data-testid="cleaning-export-range-error"
              className="text-xs text-rose-700 self-center"
            >
              From must be on or before To.
            </p>
          )}
        </div>

        {rangeInvalid || exportRejected ? null : completions.length === 0 ? (
          <p data-testid="cleaning-export-empty" className="text-sm text-gray-400">
            No completions in this date range.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="cleaning-export-table" className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Area
                  </th>
                  <th className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Cleaned by
                  </th>
                  <th className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    At
                  </th>
                  <th className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th className="py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                </tr>
              </thead>
              <tbody>
                {completions.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="py-2 px-3 text-sm text-gray-700">
                      {c.areaZone ? `${c.areaZone} — ${c.areaName}` : c.areaName}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-500">{c.cleanedByName}</td>
                    <td className="py-2 px-3 text-sm text-gray-500">
                      {formatDublinDateTime(c.cleanedAt)}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-400">{c.note ?? '—'}</td>
                    <td className="py-2 px-3 text-sm text-gray-400">{c.productUsed ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default CleaningRecords;
